import { keepPreviousData } from '@tanstack/react-query';
import type { SortingState } from '@tanstack/react-table';
import { FC, useEffect, useState } from 'react';
import {
  CustomPagination,
  serializeSort as defaultSerializeSort,
  FormattedDate,
  getIsoDateFromFormattedDate,
  Main,
  Padding,
  ProcessingEventDirectoryEntryType,
  InputProcessingEventsFilterKey,
  inputProcessingEventsFilterKeys,
  ProcessingEventsSortKey,
  Stack,
  TotalEntries,
  useDirectory,
  useInputDataProcessingEventsQuery,
  FilterTransformer,
  SortSerializer,
  serializeValueDate,
  deserializeValueDate,
  EMPTY_PAGE,
  useLoadViewState,
  useSaveDirectoryState,
  useQueryParams,
  Spinner,
  InfoIconUrl,
} from '@atlas-frontend/sdk';
import { Flex, type PaginationProps } from 'antd';
import styled from 'styled-components';
import { SectionIndexWithMenu } from '../../../common/Section';
import { useSectionBasePath } from '../../../common/SectionContext';
import { dataSections } from '../../Data';
import { InputDataHeader } from '../InputDataHeader';
import { InputDataDirectoryFilter } from '../InputDataDirectoryFilter';
import { InputDataProcessingEventsTable } from './InputDataProcessingEventsTable';
import { useToggleProcessingEventStatus } from './ToggleInputDataProcessingEventStatusSwitch';
import { useDaysToSetQueryParamsPeriod } from '../utils';

const arrayFields: InputProcessingEventsFilterKey[] = [
  'processingTypeId',
  'errorLevelId',
  'statusId',
  'valueDate',
  'valueDays',
  'startTime',
  'endTime',
];

const numericFields: InputProcessingEventsFilterKey[] = [
  'processingTypeId',
  'errorLevelId',
  'statusId',
  'valueDays',
];

const booleanFields: InputProcessingEventsFilterKey[] = [
  'userFollowedDataObjectGroups',
  'onlyLastValueDate',
];

const directoryStateStorageKey = 'inputDataErrorDirectory';

export const InputDataProcessingEventsDirectory: FC = () => {
  useDaysToSetQueryParamsPeriod();
  const [{ valueDays }] = useQueryParams();
  const { state: viewState, isQueryParamCleared } = useLoadViewState(
    directoryStateStorageKey,
    {
      sort: [{ id: 'endTime', desc: true }],
    },
  );

  if (!isQueryParamCleared) {
    return null;
  }

  if (valueDays) {
    return <Spinner />;
  }

  return (
    <InputDataProcessingEventsDirectoryInner
      initialFilters={viewState.filters}
      initialSort={viewState.sort}
      initialPagination={viewState.pagination}
    />
  );
};

interface InputDataProcessingEventsDirectoryInnerProps {
  initialFilters: Partial<Record<string, any>>;
  initialSort: SortingState;
  initialPagination: PaginationProps | undefined;
}

export const InputDataProcessingEventsDirectoryInner: FC<
  InputDataProcessingEventsDirectoryInnerProps
> = (props) => {
  const { initialFilters, initialSort, initialPagination } = props;
  const {
    headerProps,
    sidePanelFilterProps,
    tableProps,
    paginationProps,
    queryParameters,
    setTotal,
  } = useDirectory({
    queryFilterKeys: inputProcessingEventsFilterKeys,
    initialFilters,
    initialSort,
    initialPagination,
    arrayFields,
    numericFields,
    booleanFields,
    serializeFilter,
    deserializeFilter,
    serializeSort,
  });

  useSaveDirectoryState(directoryStateStorageKey, {
    paginationProps,
    queryParameters,
    tableProps,
  });

  const {
    data = EMPTY_PAGE,
    isFetching,
    refetch,
  } = useInputDataProcessingEventsQuery(queryParameters, {
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    setTotal(data?.totalElements);
  }, [setTotal, data?.totalElements]);

  const sectionBasePath = useSectionBasePath();

  const toggleStatus = useToggleProcessingEventStatus(refetch, isFetching);

  const [isFilterOpen, setIsFilterOpen] = useState(false);

  return (
    <SectionIndexWithMenu
      sections={dataSections}
      basePath={sectionBasePath}
      selectedKeys={['input-data']}
    >
      <Main>
        <Main.Col border={false}>
          <Main.Content
            padding={false}
            id="processing-errors-directory-content"
          >
            <DirectoryWrapper size="md">
              <Padding top horizontal>
                <InputDataHeader
                  valueDate={queryParameters.valueDate}
                  onFilterToggle={() => setIsFilterOpen((prev) => !prev)}
                  isFilterOpen={isFilterOpen}
                  onRefresh={refetch}
                  {...headerProps}
                  infoIconUrl={
                    InfoIconUrl.INPUT_DATA_PROCESSING_EVENTS_DIRECTORY
                  }
                />
              </Padding>
              <Padding bottom horizontal>
                <Flex vertical gap="small">
                  <InputDataProcessingEventsTable
                    {...tableProps}
                    data={data?.content ?? EMPTY_DATA}
                    isLoading={isFetching}
                    toggleStatus={toggleStatus}
                  />
                </Flex>
              </Padding>
            </DirectoryWrapper>
          </Main.Content>
          <Main.Footer>
            <TotalEntries count={data?.totalElements} />
            <FooterPagination {...paginationProps} />
          </Main.Footer>
        </Main.Col>
        {isFilterOpen && (
          <InputDataDirectoryFilter
            onClose={() => setIsFilterOpen(false)}
            onClear={sidePanelFilterProps.onClear}
            onFilter={(a) => sidePanelFilterProps.onFilter(Object(a))}
            dataManager={queryParameters.dataManager}
            outputCode={queryParameters.outputCode}
          />
        )}
      </Main>
    </SectionIndexWithMenu>
  );
};

const EMPTY_DATA: ProcessingEventDirectoryEntryType[] = [];

const dateRangeFields = ['startTime', 'endTime'];

const serializeFilter: FilterTransformer<
  string,
  InputProcessingEventsFilterKey
> = (...args) => {
  const [[id, value], ...rest] = Object.entries(
    serializeValueDate(args[0], args[1]),
  );

  if (id === 'groupDescription' || id === 'userFollowedDataObjectGroups') {
    const { userFollowedDataObjectGroups, groupDescription } = (value ||
      {}) as {
      userFollowedDataObjectGroups?: boolean;
      groupDescription?: number;
    };
    return {
      groupDescription,
      userFollowedDataObjectGroups,
    };
  }

  if (id === 'dataObjectCodeName') {
    const { userFollowedDataObjectGroups, groupDescription } = (value ||
      {}) as {
      userFollowedDataObjectGroups?: boolean;
      groupDescription?: string;
    };
    const [groupId, descriptionId] = groupDescription?.split('-') || [];
    return {
      dataObjectId:
        descriptionId || (groupId && Number(descriptionId || groupId)),
      dataObjectType: descriptionId ? 'description' : groupId && 'group',
      userFollowedDataObjectGroups,
      ...Object.fromEntries(rest),
    };
  }

  return {
    [id]: dateRangeFields.includes(id)
      ? (value as FormattedDate[] | undefined)?.map(getIsoDateFromFormattedDate)
      : value,
    ...Object.fromEntries(rest),
  };
};

const deserializeFilter: FilterTransformer<InputProcessingEventsFilterKey> = (
  id,
  value,
  allQueryFilters,
) => {
  if (id === 'dataObjectId' || id === 'userFollowedDataObjectGroups') {
    const { userFollowedDataObjectGroups, dataObjectType } =
      allQueryFilters as {
        userFollowedDataObjectGroups?: boolean;
        dataObjectType?: string;
      };
    return {
      dataObjectCodeName:
        userFollowedDataObjectGroups || dataObjectType
          ? {
              userFollowedDataObjectGroups,
              dataObjectCodeName:
                dataObjectType === 'group' ? value : `null-${value}`,
            }
          : undefined,
    };
  }

  if (id === 'dataObjectType') {
    return {};
  }

  return deserializeValueDate(id, value, allQueryFilters);
};

const serializeSort: SortSerializer<ProcessingEventsSortKey> = ({
  id,
  desc,
}) => {
  const ids = [id];

  if (id === 'createdBy') {
    ids.splice(0, 1, 'creatorUsername', 'creatorFirstName', 'creatorLastName');
  }

  return ids.map((id) => defaultSerializeSort({ id, desc }));
};

const FooterPagination = styled(CustomPagination)`
  margin-left: auto;
`;

const DirectoryWrapper = styled(Stack)`
  width: 100%;
  max-width: 100em;
  height: 100%;
  margin-right: auto;
  margin-left: auto;
`;
